import { PreventTransitionPrompt } from "components";
import { type PackAccountsContextItem } from "components/assignmentModals/groupTemplatePacksConfirmationModal/types";
import { NotifyStepSwitch } from "components/notifyStep/NotifyStepSwitch";
import NotifyWizardStep from "components/notifyStep/NotifyWizardStep/NotifyWizardStep";
import { Section, WizardNew } from "components/wizard";
import { TemplateTypes } from "enums";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { initialNotifyConfigDefault } from "features/SystemNotifications/config";
import { withNotifyConfig, type WithNotifyConfigProps } from "features/SystemNotifications/containers/withNotifyConfig";
import { useNotifyStep } from "features/SystemNotifications/hooks/useNotifyStep";
import { type NotifySettings } from "features/SystemNotifications/types";
import { useObservable } from "hooks/useObservable";
import { useWizardStepsManager } from "hooks/useWizardStepsManager";
import { bindAction, type NotifyStepSettings } from "interfaces";
import { type AddContact, type AddContactPayload } from "interfaces/contact";
import type NewContact from "interfaces/NewContact";
import { useCallback, useEffect, useMemo, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { useNavigate, useResolvedPath } from "react-router";
import { ExistingAccount } from "../../Configuration/ExistingAccount";
import { saveTemplatesPacks } from "../../state/thunks/AccountTemplatesThunk";
import ConfirmTemplatesPacksModal from "../../Templates/ConfirmTemplatesPacksModal";
import ManagedByContacts from "../ManagedByContacts/ManagedByContacts";
import { addManagedByContactsThunk, fetchChildAccountRoles } from "../state/thunks/createAccountThunk";
import CreateAccountTemplates from "../Templates/CreateAccountTemplates";
import { addDomainToAccount } from "../../state/thunks/accountDomainsThunk";
import { reset as resetCustomers } from "../../../People/GroupTemplate/state/TemplateAvailableCustomersSlice";

interface Props extends WithNotifyConfigProps<NotifySettings>, PropsFromRedux {
  account: { name: string; id: number; logoUri: Nullable<string> };
  onRemoveName: () => void;
}

enum Pages {
  Configuration,
  Contacts,
  Templates,
  Notify,
}

const pageIndexes = [Pages.Configuration, Pages.Contacts, Pages.Templates, Pages.Notify];
const transitionalPromptIndexes = [Pages.Contacts, Pages.Templates, Pages.Notify];

const buildAddContactPayload = (
  accountId: number,
  accountName: string,
  contacts: NewContact[],
  notificationSettings?: NotifyStepSettings,
  templateIds?: number[],
): AddContactPayload => {
  return {
    accountId,
    name: accountName,
    contacts: contacts.map<AddContact>((c) => ({
      firstName: c.firstName,
      lastName: c.lastName,
      email: c.email,
      department: c.department,
      jobTitle: c.title,
      roleIds: c.roles.map((r) => r.id),
    })),
    notificationSettings,
    templateIds,
  };
};

const ConfigureExisting = (props: Props) => {
  const {
    onRemoveName,
    account,
    addTemplatePacksToAccount,
    childRoles,
    isLoadingChildRoles,
    fetchChildAccountRoles,
    addDomainToAccountAction,
    resetCustomers,
    addContacts,
    moboAccountId,
    ...notifyProps
  } = props;

  const { notifyConfigPayload: notifyStepSettings, notifyConfig } = notifyProps;
  const { notifyControllerRef, notifyStepDataValid, notifyStepLoaded, onNotifyStepLoaded, setNotifyStepDataValid } =
    useNotifyStep();
  const [wizardPages, { onNext, onPrevious, onFinish }, subscribeOnActiveIndexChanged] =
    useWizardStepsManager(pageIndexes);
  const onTriggerConfirmLicensingObserver = useObservable();
  const rootPath = useResolvedPath("..").pathname;
  const navigate = useNavigate();
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
  const [contacts, setContacts] = useState<NewContact[]>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<number[]>([]);
  const [activeTab, setActiveTab] = useState<Pages>(Pages.Configuration);
  useEffect(() => {
    const handler = (_: Pages, activeStepIndex: Pages) => {
      setActiveTab(activeStepIndex);
    };

    return subscribeOnActiveIndexChanged(handler);
  }, [subscribeOnActiveIndexChanged]);

  useEffect(() => {
    return () => {
      resetCustomers();
    };
  }, [resetCustomers]);

  const handleFinish = useCallback(() => {
    onFinish();
    navigate(rootPath);
  }, [rootPath, navigate, onFinish]);

  const handleOnFinish = useCallback(async () => {
    setSaveInProgress(true);
    if (contacts.length) {
      const notificationSettings = await notifyControllerRef.current?.getSettings(
        notifyStepSettings.shouldNotify,
        notifyStepSettings.communicationChannel,
      );

      const payload = buildAddContactPayload(
        account.id,
        account.name,
        contacts,
        notificationSettings,
        selectedTemplates,
      );
      await addContacts(payload);
    }

    if (selectedTemplates.length) {
      onTriggerConfirmLicensingObserver.notify((packs: PackAccountsContextItem[]) => {
        addTemplatePacksToAccount(account.id, packs);
        addDomainToAccountAction([account.id]);
        handleFinish();
      }, selectedTemplates);
    } else {
      handleFinish();
    }
  }, [
    account.id,
    account.name,
    addContacts,
    notifyControllerRef,
    notifyStepSettings,
    addTemplatePacksToAccount,
    selectedTemplates,
    contacts,
    onTriggerConfirmLicensingObserver,
    handleFinish,
    addDomainToAccountAction,
    setSaveInProgress,
  ]);

  const onContactsChanged = (newContacts: NewContact[]) => {
    setContacts(newContacts);
  };

  const renderPreventTransitionModal = useCallback(() => {
    return (
      <PreventTransitionPrompt
        when={!saveInProgress && transitionalPromptIndexes.includes(activeTab)}
        title={"Exit Without Saving?"}
        message={"Are you sure you want to exit without saving this account? All information entered will be lost."}
      />
    );
  }, [activeTab, saveInProgress]);

  const finishButtonLabel = useMemo(() => {
    const isNotifyTab = activeTab === Pages.Notify;
    if (isNotifyTab) {
      return notifyStepSettings.shouldNotify ? "Finish & Send" : "Finish";
    }
    return "Next";
  }, [activeTab, notifyStepSettings.shouldNotify]);
  return (
    <>
      <WizardNew
        id="create-account-wizard"
        className="create-account"
        title="Create Account"
        finishButtonLabel={finishButtonLabel}
        isSaveInProgress={false}
        onCancel={() => navigate(rootPath)}
        onFinish={handleOnFinish}
        onProgress={onNext}
        onRegress={onPrevious}
      >
        <Section label="Configuration" required>
          <ExistingAccount {...account} acceptHandlers={wizardPages[Pages.Configuration]} onRemoved={onRemoveName} />
        </Section>

        <Section label="Contacts" required isLocked={contacts.length === 0}>
          <ManagedByContacts
            contacts={contacts}
            onContactsChanged={onContactsChanged}
            childRoles={childRoles}
            accountId={account.id}
            moboAccountId={moboAccountId}
            fetchChildAccountRoles={fetchChildAccountRoles}
            isLoadingChildRoles={isLoadingChildRoles}
          />
        </Section>

        <Section label="Templates" required isLocked={selectedTemplates.length === 0}>
          <CreateAccountTemplates
            acceptHandlers={wizardPages[Pages.Templates]}
            accountId={account.id}
            onSelectedChanged={setSelectedTemplates}
          />
        </Section>
        <Section
          label="Notify"
          preRender
          isLocked={notifyStepSettings.shouldNotify && !notifyStepLoaded && !notifyStepDataValid}
        >
          <NotifyWizardStep
            templateType={TemplateTypes.WelcomeEmail}
            onLoaded={onNotifyStepLoaded}
            onValidChange={setNotifyStepDataValid}
            shouldNotify={notifyStepSettings.shouldNotify}
            communicationChannel={notifyStepSettings.communicationChannel}
            renderSwitch={(switchProps) => (
              <NotifyStepSwitch
                config={notifyConfig}
                onNotifyConfigChange={notifyStepSettings.setNotifyConfig}
                switchProps={switchProps}
              />
            )}
          />
        </Section>
      </WizardNew>
      {renderPreventTransitionModal()}
      <ConfirmTemplatesPacksModal onTriggerModalObserver={onTriggerConfirmLicensingObserver} />
    </>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => ({
  childRoles: state.accounts.creation.childRoles.items,
  isLoadingChildRoles: state.accounts.creation.childRoles.isLoading,
  moboAccountId: state.accounts.edit.accountInfo.accountConfiguration.id,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  addTemplatePacksToAccount: bindAction(saveTemplatesPacks, dispatch),
  fetchChildAccountRoles: bindAction(fetchChildAccountRoles, dispatch),
  addContacts: bindAction(addManagedByContactsThunk, dispatch),
  addDomainToAccountAction: bindAction(addDomainToAccount, dispatch),
  resetCustomers: () => dispatch(resetCustomers()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(withNotifyConfig(ConfigureExisting, initialNotifyConfigDefault));

export default ConnectedComponent;
